import React from 'react';
import { Box, Container, Typography, Tooltip } from '@mui/material';
import {
  Update as UpdateIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MORTGAGE_RATES } from '../../data/mortgageRates';

const DATE_LOCALES = { es: 'es-CA', en: 'en-CA', fr: 'fr-CA' };
const formatDate = (dateStr, lang = 'es') => {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString(DATE_LOCALES[lang] || 'es-CA', { day: 'numeric', month: 'long', year: 'numeric' });
};

const TREND_SYMBOLS = {
  down:   { symbol: '\u25BC', color: '#2E7D32' },
  up:     { symbol: '\u25B2', color: '#C62828' },
  stable: { symbol: '\u2014', color: '#6B7280' },
};

const TrendArrow = ({ trendKey, t }) => {
  const trend = MORTGAGE_RATES.trends?.[trendKey];
  if (!trend) return null;
  const cfg = TREND_SYMBOLS[trend.trend] || TREND_SYMBOLS.stable;
  const label = t(`mortgagePage.trend.${trend.trend}`, { prev: trend.previous });
  return (
    <Tooltip title={label} arrow enterTouchDelay={0}>
      <Typography
        component="span"
        sx={{ fontSize: '12px', fontWeight: 700, color: cfg.color, ml: 0.5, cursor: 'default', verticalAlign: 'middle' }}
      >
        {cfg.symbol}
      </Typography>
    </Tooltip>
  );
};

const RateSnapshot = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const rates = MORTGAGE_RATES;
  const lowestFixed = rates.quebec.bestRates.find((r) => r.term === '5 a\u00f1os fijo')?.rate;
  const lowestVariable = rates.quebec.bestRates.find((r) => r.term === '5 a\u00f1os variable')?.rate;
  const fixedSource = rates.quebec.bestRates.find((r) => r.term === '5 a\u00f1os fijo')?.source;
  const variableSource = rates.quebec.bestRates.find((r) => r.term === '5 a\u00f1os variable')?.source;

  const bocTooltipText = t('mortgagePage.banner.bocTooltip')
    + (rates.nextBoCDecision ? t('mortgagePage.banner.nextDecision', { date: formatDate(rates.nextBoCDecision, lang) }) : '');

  return (
    <Box component="section" sx={{ py: { xs: 1.5, md: 2 } }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            bgcolor: '#F8FAFC',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 2.5 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flexWrap: 'wrap',
              justifyContent: { md: 'space-between' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {/* Best fixed */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>
                {t('mortgagePage.banner.bestFixed5yr')}
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1E3A5F', fontVariantNumeric: 'tabular-nums' }}>
                {lowestFixed}%
              </Typography>
              <TrendArrow trendKey="fixed5" t={t} />
              {fixedSource && (
                <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>{fixedSource}</Typography>
              )}
            </Box>

            {/* Best variable */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>
                {t('mortgagePage.banner.bestVariable5yr')}
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1E3A5F', fontVariantNumeric: 'tabular-nums' }}>
                {lowestVariable}%
              </Typography>
              <TrendArrow trendKey="variable5" t={t} />
              {variableSource && (
                <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>{variableSource}</Typography>
              )}
            </Box>

            {/* BoC rate */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>
                {t('mortgagePage.banner.bankOfCanada')}
              </Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1E3A5F', fontVariantNumeric: 'tabular-nums' }}>
                {rates.bankOfCanadaRate}%
              </Typography>
              <TrendArrow trendKey="bocRate" t={t} />
              <Tooltip title={bocTooltipText} arrow enterTouchDelay={0}>
                <InfoOutlinedIcon sx={{ fontSize: 13, color: '#9CA3AF', cursor: 'help', verticalAlign: 'middle' }} />
              </Tooltip>
            </Box>

            {/* Updated + sources */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <UpdateIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
              <Typography sx={{ fontSize: '12px', color: '#9CA3AF', lineHeight: 1.4 }}>
                {t('mortgagePage.snapshot.updated', { date: formatDate(rates.lastUpdated, lang) })}
                {' \u00b7 '}
                {t('mortgagePage.snapshot.sources')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default RateSnapshot;
